import * as React from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Button, FormCheckbox } from "@jhool-io/fe-components";
import {
    ConsentAttachmentsStepConfig,
    ConsentPageConfig,
    ConsentPaymentCardStepConfig,
    IConsentDraftData,
    IConsentDraftPayload,
    IConsentFormAnswers,
} from "../../types/client-forms.types";
import ChevronLeftIcon from "../../../../components/Icons/ChevronLeft";
import ChevronRightIcon from "../../../../components/Icons/ChevronRight";
import {
    useSaveConsentDraft,
    useUploadConsentAttachments,
} from "../../hooks/client-forms.mutations";
import useToast from "../../../../hooks/useToast";
import ConsentFormContent from "./ConsentFormContent";
import { useFetchPaymentCards } from "../../../billing/hooks/billing.queries";
import AddCardForm from "../../../payments/components/PaymentCards/AddCardForm";
import { useAcceptJS } from "../../../../hooks/useAcceptJS/useAcceptJS";
import ConsentPaymentCardStep from "./ConsentPaymentCardStep";
import ConsentPageFields from "./ConsentPageFields";
import ConsentAttachmentsStep from "./ConsentAttachmentsStep";

export interface ConsentPaymentCardConfig {
    hasValidCard: boolean;
    isLoading: boolean;
    isError: boolean;
    isFetching: boolean;
    isAddingCard: boolean;
    isReady: boolean;
    onRetry: () => void;
    renderForm: (onSaved: () => void | Promise<void>) => React.ReactNode;
}

function getProfileInitials(fullName: string): string {
    const nameParts = fullName.trim().split(/\s+/).filter(Boolean);
    if (nameParts.length > 1) {
        return `${nameParts[0][0]}.${nameParts[nameParts.length - 1][0]}.`.toUpperCase();
    }
    if (nameParts.length === 1) {
        return `${nameParts[0][0]}.`.toUpperCase();
    }
    return "";
}

function getFirstInitials(...values: Array<string | undefined>): string {
    return values.find((value) => value?.trim())?.trim() ?? "";
}

function findPreviousInitials(
    pages: ConsentPageConfig[],
    pageIndex: number,
    values: Record<string, string>
): string {
    for (let index = pageIndex; index >= 0; index -= 1) {
        const page = pages[index];
        if (page.requiresInitials) {
            const key = page.initialsFieldKey ?? `page_${index}_initials`;
            const value = values[key]?.trim();
            if (value) return value;
        }
    }
    return "";
}

interface ConsentStepperProps {
    consentId?: string;
    brand?: string;
    formType?: string | null;
    variant?: "adult" | "minor";
    pages: ConsentPageConfig[];
    clientFullName: string;
    clientDateOfBirth: string;
    initialDraftData?: IConsentDraftData | null;
    isSubmitting: boolean;
    onSubmit: (
        answers: IConsentFormAnswers,
        emailCopyRequested: boolean
    ) => Promise<void>;
    onSaveDraft?: (draft: IConsentDraftPayload) => Promise<void>;
    isSavingDraft?: boolean;
    paymentCard?: ConsentPaymentCardConfig | null;
    paymentCardStepConfig?: ConsentPaymentCardStepConfig | null;
    attachmentsStepConfig?: ConsentAttachmentsStepConfig | null;
    initialUploadedFieldIds?: Set<string>;
    initialUploadedFiles?: Record<string, string[]>;
    onUploadAttachment?: (fieldId: string, files: File[]) => Promise<void>;
}

export default function ConsentStepper({
    consentId,
    brand,
    formType,
    variant,
    pages,
    clientFullName,
    clientDateOfBirth,
    initialDraftData,
    isSubmitting,
    onSubmit,
    onSaveDraft,
    isSavingDraft = false,
    paymentCard,
    paymentCardStepConfig,
    attachmentsStepConfig,
    initialUploadedFieldIds,
    initialUploadedFiles,
    onUploadAttachment,
}: ConsentStepperProps) {
    const profileInitials = getProfileInitials(clientFullName);
    const cardStepAfterPage =
        paymentCardStepConfig?.insertAfterPageIndex ??
        Math.max(pages.length - 2, 0);
    const initialPageInitials = initialDraftData?.values.page_initials ?? {};
    const initialCardAuthorization =
        initialDraftData?.values.payment_card_authorization;
    const initialCardStepInitials = getFirstInitials(
        initialCardAuthorization?.initials,
        findPreviousInitials(pages, cardStepAfterPage, initialPageInitials),
        variant === "minor" ? "" : profileInitials
    );

    const [currentPage, setCurrentPage] = React.useState(() => {
        if (!initialDraftData) return 0;
        return Math.min(
            Math.max(initialDraftData.current_step, 0),
            Math.max(pages.length - 1, 0)
        );
    });
    const [pageInitials, setPageInitials] =
        React.useState<Record<string, string>>(initialPageInitials);
    const [pageSignatures, setPageSignatures] = React.useState<
        Record<string, string>
    >(initialDraftData?.values.page_signatures || {});
    const [pageCheckboxes, setPageCheckboxes] = React.useState<
        Record<string, boolean>
    >(initialDraftData?.values.page_checkboxes || {});
    const [pageFullName, setPageFullName] = React.useState<
        Record<string, string>
    >(initialDraftData?.values.page_full_names || {});
    const [pageCustodianFullNames, setPageCustodianFullNames] = React.useState<
        Record<string, string>
    >(initialDraftData?.values.page_custodian_full_names || {});
    const [pageMultiCheckboxes, setPageMultiCheckboxes] = React.useState<
        Record<string, boolean>
    >(initialDraftData?.values.page_multi_checkboxes || {});
    const [pageDates, setPageDates] = React.useState<Record<string, Date>>(
        !initialDraftData?.values.page_dates
            ? {}
            : Object.fromEntries(
                  Object.entries(initialDraftData.values.page_dates).map(
                      ([k, v]) => [k, new Date(v)]
                  )
              )
    );
    const [pageDatesOfBirth, setPageDatesOfBirth] = React.useState<
        Record<string, Date>
    >(
        !initialDraftData?.values.page_date_of_births
            ? {}
            : Object.fromEntries(
                  Object.entries(
                      initialDraftData.values.page_date_of_births
                  ).map(([k, v]) => [k, new Date(`${v.slice(0, 10)}T00:00:00`)])
              )
    );
    const [pageCustodianDatesOfBirth, setPageCustodianDatesOfBirth] =
        React.useState<Record<string, Date>>(
            !initialDraftData?.values.page_custodian_dates_of_birth
                ? {}
                : Object.fromEntries(
                      Object.entries(
                          initialDraftData.values.page_custodian_dates_of_birth
                      ).map(([key, value]) => [
                          key,
                          new Date(`${value.slice(0, 10)}T00:00:00`),
                      ])
                  )
        );
    const [fieldError, setFieldError] = React.useState("");
    const [isCardStep, setIsCardStep] = React.useState(false);
    const [hasAddedCard, setHasAddedCard] = React.useState(false);
    const [cardStepInitials, setCardStepInitials] = React.useState(
        initialCardStepInitials
    );
    const [cardStepAdditionalInitials, setCardStepAdditionalInitials] =
        React.useState<Record<string, string>>(
            initialCardAuthorization?.additional_initials ?? {}
        );
    const [emailCopyRequested, setEmailCopyRequested] = React.useState(false);
    const [isAttachmentsStep, setIsAttachmentsStep] = React.useState(false);
    const [isUploadingAttachments, setIsUploadingAttachments] =
        React.useState(false);
    const [uploadedFieldIds, setUploadedFieldIds] = React.useState<Set<string>>(
        () =>
            initialUploadedFieldIds ??
            new Set(Object.keys(initialUploadedFiles ?? {}))
    );
    const [uploadedFiles, setUploadedFiles] = React.useState<
        Record<string, string[]>
    >(initialUploadedFiles ?? {});
    const [pendingAttachmentFiles, setPendingAttachmentFiles] = React.useState<
        Record<string, File[]>
    >({});

    const { toast } = useToast();
    const queryClient = useQueryClient();
    const saveConsentDraft = useSaveConsentDraft();
    const uploadAttachments = useUploadConsentAttachments();
    const hasAttachmentsStep = Boolean(attachmentsStepConfig?.fields?.length);
    const usesDefaultPaymentCard = paymentCard === undefined;
    const {
        data: paymentCards,
        isLoading: isPaymentCardsLoading,
        isError: isPaymentCardsError,
        isFetching: isPaymentCardsFetching,
        refetch: refetchPaymentCards,
    } = useFetchPaymentCards(usesDefaultPaymentCard);
    const { isReady: isAcceptJSReady } = useAcceptJS({
        useDefaultCredentials: usesDefaultPaymentCard,
    });
    const isAddingDefaultCard =
        useIsMutating({ mutationKey: ["add-payment-card"] }) > 0;

    const savedCards = paymentCards?.data ?? [];
    const defaultPaymentCard: ConsentPaymentCardConfig = {
        hasValidCard:
            savedCards.length > 0 &&
            savedCards.some((card) => !card.is_card_expired),
        isLoading: isPaymentCardsLoading,
        isError: isPaymentCardsError,
        isFetching: isPaymentCardsFetching,
        isAddingCard: isAddingDefaultCard,
        isReady: isAcceptJSReady,
        onRetry: () => {
            refetchPaymentCards();
        },
        renderForm: (onSaved) => (
            <AddCardForm
                formId="consent-add-card-form"
                consentId={consentId}
                onSaved={onSaved}
            />
        ),
    };
    const paymentCardState = usesDefaultPaymentCard
        ? defaultPaymentCard
        : paymentCard;
    const requiresPaymentCard =
        Boolean(paymentCardState) &&
        !paymentCardState?.isLoading &&
        !paymentCardState?.isError &&
        !paymentCardState?.hasValidCard &&
        !hasAddedCard;
    const getInitialsForPage = (
        pageIndex: number,
        values: Record<string, string>
    ): string => {
        const config = pages[pageIndex];
        const key = config.initialsFieldKey ?? `page_${pageIndex}_initials`;
        if (values[key] !== undefined) {
            return values[key];
        }
        const previousValue = findPreviousInitials(
            pages,
            pageIndex - 1,
            values
        );
        if (previousValue) return previousValue;
        return variant === "minor" ? "" : profileInitials;
    };

    const prepareCardStepInitials = () => {
        if (!cardStepInitials.trim()) {
            setCardStepInitials(getInitialsForPage(currentPage, pageInitials));
        }

        const fields = paymentCardStepConfig?.additionalInitialsFields ?? [];
        if (fields.length === 0) return;

        setCardStepAdditionalInitials((previous) => {
            const next = { ...previous };
            fields.forEach((field, fieldIndex) => {
                if (next[field.key]?.trim()) return;
                for (let index = currentPage; index >= 0; index -= 1) {
                    const sourceField =
                        pages[index].additionalInitialsFields?.[fieldIndex];
                    const value = sourceField
                        ? pageInitials[sourceField.key]?.trim()
                        : "";
                    if (value) {
                        next[field.key] = value;
                        break;
                    }
                }
            });
            return next;
        });
    };

    const defaultFullNameForPage = (config: ConsentPageConfig): string => {
        if (variant !== "minor") return clientFullName;
        return config.fullNameLabel?.toLowerCase().includes("client")
            ? clientFullName
            : "";
    };

    const effectivePageInitials: Record<string, string> = {
        ...pageInitials,
    };
    const effectivePageFullNames: Record<string, string> = {
        ...pageFullName,
    };
    pages.forEach((config, index) => {
        if (config.requiresInitials) {
            const key = config.initialsFieldKey ?? `page_${index}_initials`;
            effectivePageInitials[key] = getInitialsForPage(
                index,
                pageInitials
            );
        }
        if (config.requireFullName) {
            const key = config.fullNameFieldKey ?? `page_${index}_full_name`;
            effectivePageFullNames[key] =
                pageFullName[key] ?? defaultFullNameForPage(config);
        }
    });

    const page = pages[currentPage];

    const initialsKey = page.initialsFieldKey ?? `page_${currentPage}_initials`;
    const signatureKey =
        page.signatureFieldKey ?? `page_${currentPage}_signature`;
    const checkboxKey = page.checkboxFieldKey ?? `page_${currentPage}_checkbox`;
    const fullNameKey =
        page.fullNameFieldKey ?? `page_${currentPage}_full_name`;
    const dateKey = page.dateFieldKey ?? `page_${currentPage}_date`;
    const dateOfBirthKey =
        page.dateOfBirthFieldKey ?? `page_${currentPage}_date_of_birth`;
    const custodianFullNameKey =
        page.custodianFullNameFieldKey ??
        `page_${currentPage}_custodian_full_name`;
    const custodianDateOfBirthKey =
        page.custodianDateOfBirthFieldKey ??
        `page_${currentPage}_custodian_date_of_birth`;

    const currentInitials = getInitialsForPage(currentPage, pageInitials);
    const currentCheckbox = pageCheckboxes[checkboxKey] ?? false;
    const currentFullName =
        pageFullName[fullNameKey] ?? defaultFullNameForPage(page);
    const currentDate = pageDates[dateKey] ?? new Date();
    const profileDateOfBirth = clientDateOfBirth
        ? new Date(`${clientDateOfBirth.slice(0, 10)}T00:00:00`)
        : null;
    const currentDateOfBirth =
        pageDatesOfBirth[dateOfBirthKey] ??
        (profileDateOfBirth && !Number.isNaN(profileDateOfBirth.getTime())
            ? profileDateOfBirth
            : null);
    const currentCustodianFullName =
        pageCustodianFullNames[custodianFullNameKey] ?? "";
    const currentCustodianDateOfBirth =
        pageCustodianDatesOfBirth[custodianDateOfBirthKey] ?? null;
    const dateOnly = (date: Date): string => {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const validateCurrentPage = (): boolean => {
        if (page.requiresInitials && !currentInitials.trim()) {
            setFieldError("Please enter your initials to continue.");
            return false;
        }
        if (
            page.additionalInitialsFields?.some(
                (field) =>
                    field.required !== false && !pageInitials[field.key]?.trim()
            )
        ) {
            setFieldError("Please enter all required initials to continue.");
            return false;
        }
        if (page.requiresSignature) {
            if (!pageSignatures[signatureKey]) {
                setFieldError("Please provide your signature to continue.");
                return false;
            }
        }
        if (page.requireFullName && !currentFullName.trim()) {
            setFieldError("Please enter your full name to continue.");
            return false;
        }
        if (
            page.additionalFullNameFields?.some(
                (field) =>
                    field.required !== false && !pageFullName[field.key]?.trim()
            )
        ) {
            setFieldError("Please enter all required names to continue.");
            return false;
        }
        if (
            page.requiresCustodianFullName &&
            !currentCustodianFullName.trim()
        ) {
            setFieldError("Please enter the legal custodian name to continue.");
            return false;
        }
        if (
            page.additionalSignatureFields?.some(
                (field) =>
                    field.required !== false && !pageSignatures[field.key]
            )
        ) {
            setFieldError(
                "Please provide all required signatures to continue."
            );
            return false;
        }
        if (page.requiresCheckbox && !currentCheckbox) {
            setFieldError("Please check the box to continue.");
            return false;
        }
        if (page.requiresDateOfBirth && !currentDateOfBirth) {
            setFieldError("Please enter your date of birth to continue.");
            return false;
        }
        if (page.requiresCustodianDateOfBirth && !currentCustodianDateOfBirth) {
            setFieldError(
                "Please enter the legal custodian date of birth to continue."
            );
            return false;
        }
        return true;
    };

    const configuredCardAdditionalInitials = Object.fromEntries(
        (paymentCardStepConfig?.additionalInitialsFields ?? []).flatMap(
            (field) => {
                const value = cardStepAdditionalInitials[field.key];
                return value === undefined ? [] : [[field.key, value]];
            }
        )
    );
    const paymentCardAuthorization = {
        initials: cardStepInitials.trim(),
        ...(Object.keys(configuredCardAdditionalInitials).length > 0
            ? { additional_initials: configuredCardAdditionalInitials }
            : {}),
    };

    const buildDraftPayload = (
        stepIndex: number,
        completedPageNumber: number,
        completedPageType: "consent" | "payment_card_authorization"
    ): IConsentDraftPayload => ({
        current_step: stepIndex,
        completed_page_number: completedPageNumber,
        completed_page_type: completedPageType,
        values: {
            page_initials: effectivePageInitials,
            page_full_names: effectivePageFullNames,
            page_custodian_full_names: pageCustodianFullNames,
            page_custodian_dates_of_birth: Object.fromEntries(
                Object.entries(pageCustodianDatesOfBirth).map(
                    ([key, value]) => [key, dateOnly(value)]
                )
            ),
            page_checkboxes: pageCheckboxes,
            page_multi_checkboxes: pageMultiCheckboxes,
            page_signatures: pageSignatures,
            page_dates: Object.fromEntries(
                Object.entries(pageDates).map(([k, v]) => [k, v.toISOString()])
            ),
            page_date_of_births: Object.fromEntries(
                pages.flatMap((config, index) => {
                    if (!config.requiresDateOfBirth) return [];
                    const key =
                        config.dateOfBirthFieldKey ??
                        `page_${index}_date_of_birth`;
                    const value = pageDatesOfBirth[key] ?? profileDateOfBirth;
                    return value ? [[key, dateOnly(value)]] : [];
                })
            ),
            ...(paymentCardStepConfig &&
            (cardStepInitials.trim() ||
                Object.values(configuredCardAdditionalInitials).some((value) =>
                    value.trim()
                ))
                ? {
                      payment_card_authorization: paymentCardAuthorization,
                  }
                : {}),
        },
    });

    const saveDraft = async (
        stepIndex: number,
        completedPageNumber: number,
        completedPageType: "consent" | "payment_card_authorization" = "consent"
    ): Promise<boolean> => {
        if (!consentId) {
            return false;
        }

        try {
            const draft = buildDraftPayload(
                stepIndex,
                completedPageNumber,
                completedPageType
            );
            if (onSaveDraft) {
                await onSaveDraft(draft);
            } else {
                await saveConsentDraft.mutateAsync({ consentId, data: draft });
            }
            return true;
        } catch (err) {
            const message =
                (err as AxiosError<{ message?: string }>).response?.data
                    ?.message ||
                "Could not save your progress. Please try again.";

            toast({
                mode: "error",
                message,
            });
            return false;
        }
    };

    const handleUpload = async (
        fieldId: string,
        files: File[]
    ): Promise<void> => {
        if (onUploadAttachment) {
            await onUploadAttachment(fieldId, files);
            setUploadedFieldIds((prev) => new Set([...prev, fieldId]));
            return;
        }
        if (!consentId) {
            throw new Error("A consent ID is required to upload documents.");
        }
        await uploadAttachments.mutateAsync({ consentId, fieldId, files });
        setUploadedFieldIds((prev) => new Set([...prev, fieldId]));
    };

    const handleNext = async () => {
        if (isAttachmentsStep) {
            const missingRequired = attachmentsStepConfig?.fields.filter(
                (field) =>
                    field.required &&
                    !uploadedFieldIds.has(field.fieldId) &&
                    !(pendingAttachmentFiles[field.fieldId]?.length ?? 0)
            );
            if (missingRequired?.length) {
                setFieldError(
                    "Please upload all required documents to continue."
                );
                return;
            }

            setIsUploadingAttachments(true);
            try {
                await Object.entries(pendingAttachmentFiles)
                    .filter(([, files]) => files.length > 0)
                    .reduce(
                        (chain, [fieldId, files]) =>
                            chain.then(async () => {
                                await handleUpload(fieldId, files);
                                setUploadedFiles((previous) => ({
                                    ...previous,
                                    [fieldId]: [
                                        ...(previous[fieldId] ?? []),
                                        ...files.map((file) => file.name),
                                    ],
                                }));
                                setPendingAttachmentFiles((previous) => ({
                                    ...previous,
                                    [fieldId]: [],
                                }));
                            }),
                        Promise.resolve() as Promise<void>
                    );
            } catch (err) {
                const message =
                    (err as AxiosError<{ message?: string }>).response?.data
                        ?.message ??
                    "Could not upload your documents. Please try again.";
                setFieldError(message);
                return;
            } finally {
                setIsUploadingAttachments(false);
            }

            setFieldError("");
            setIsAttachmentsStep(false);
            setCurrentPage(pages.length - 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        if (!validateCurrentPage()) return;

        if (currentPage === cardStepAfterPage && requiresPaymentCard) {
            const didSaveDraft = await saveDraft(currentPage, currentPage + 1);
            if (!didSaveDraft) {
                return;
            }
            prepareCardStepInitials();
            setFieldError("");
            setIsCardStep(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        if (currentPage === cardStepAfterPage && hasAttachmentsStep) {
            const didSaveDraft = await saveDraft(currentPage, currentPage + 1);
            if (!didSaveDraft) {
                return;
            }
            setFieldError("");
            setIsAttachmentsStep(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        const nextStep = currentPage + 1;
        const didSaveDraft = await saveDraft(nextStep, currentPage + 1);
        if (!didSaveDraft) {
            return;
        }

        setFieldError("");
        setCurrentPage(nextStep);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleBack = () => {
        setFieldError("");
        if (isAttachmentsStep) {
            setIsAttachmentsStep(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (isCardStep) {
            setIsCardStep(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (currentPage === pages.length - 1 && hasAttachmentsStep) {
            setCurrentPage(Math.max(pages.length - 2, 0));
            setIsAttachmentsStep(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        setCurrentPage((prev) => prev - 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSubmit = async () => {
        if (!validateCurrentPage()) return;

        const completedPageNumber = cardStepInitials.trim()
            ? currentPage + 2
            : currentPage + 1;
        const didSaveDraft = await saveDraft(currentPage, completedPageNumber);
        if (!didSaveDraft) {
            return;
        }

        setFieldError("");
        try {
            const answers: IConsentFormAnswers = {
                page_initials: effectivePageInitials,
                page_full_names: effectivePageFullNames,
                page_custodian_full_names: pageCustodianFullNames,
                page_custodian_dates_of_birth: Object.fromEntries(
                    Object.entries(pageCustodianDatesOfBirth).map(
                        ([key, value]) => [key, dateOnly(value)]
                    )
                ),
                page_checkboxes: pageCheckboxes,
                page_multi_checkboxes: pageMultiCheckboxes,
                page_dates: Object.fromEntries(
                    pages.flatMap((config, index) => {
                        if (!config.requiresDate) return [];
                        const key = config.dateFieldKey ?? `page_${index}_date`;
                        return [[key, dateOnly(pageDates[key] ?? new Date())]];
                    })
                ),
                page_date_of_births: Object.fromEntries(
                    pages.flatMap((config, index) => {
                        if (!config.requiresDateOfBirth) return [];
                        const key =
                            config.dateOfBirthFieldKey ??
                            `page_${index}_date_of_birth`;
                        const value =
                            pageDatesOfBirth[key] ?? currentDateOfBirth;
                        return value ? [[key, dateOnly(value)]] : [];
                    })
                ),
                page_signatures: pageSignatures,
                ...(paymentCardStepConfig && cardStepInitials.trim()
                    ? {
                          payment_card_authorization: paymentCardAuthorization,
                      }
                    : {}),
            };
            await onSubmit(answers, emailCopyRequested);
            queryClient.invalidateQueries({ queryKey: ["client-consents"] });
            queryClient.invalidateQueries({ queryKey: ["consent"] });
        } catch (err) {
            toast({
                mode: "error",
                message:
                    err instanceof Error
                        ? err.message
                        : "Could not prepare your consent form. Please try again.",
            });
        }
    };

    const isDraftSaving = onSaveDraft
        ? isSavingDraft
        : saveConsentDraft.isLoading;
    const isPaymentCardUnavailable =
        paymentCardState?.isLoading ||
        paymentCardState?.isFetching ||
        paymentCardState?.isError;
    const isPaymentCardBlocked =
        Boolean(paymentCardState) && Boolean(isPaymentCardUnavailable);
    const isBusy =
        isSubmitting ||
        isDraftSaving ||
        isPaymentCardBlocked ||
        isUploadingAttachments ||
        uploadAttachments.isLoading;
    let nextButtonLabel = "Next";
    if (isUploadingAttachments) {
        nextButtonLabel = "Uploading...";
    } else if (isDraftSaving) {
        nextButtonLabel = "Saving...";
    }
    const isLastFormPage =
        currentPage === pages.length - 1 && !isCardStep && !isAttachmentsStep;
    const isSubmitStep = isLastFormPage;
    const totalSteps =
        pages.length +
        (requiresPaymentCard ? 1 : 0) +
        (hasAttachmentsStep ? 1 : 0);
    let activeStep: number;
    if (isAttachmentsStep) {
        activeStep = totalSteps - 1;
    } else if (isCardStep) {
        activeStep = cardStepAfterPage + 2;
    } else {
        activeStep =
            currentPage +
            1 +
            (requiresPaymentCard && currentPage > cardStepAfterPage ? 1 : 0) +
            (hasAttachmentsStep && currentPage === pages.length - 1 ? 1 : 0);
    }

    const handleCardSaved = async () => {
        const didSaveDraft = await saveDraft(
            currentPage,
            cardStepAfterPage + 2,
            "payment_card_authorization"
        );
        if (!didSaveDraft) {
            return;
        }

        setHasAddedCard(true);
        setIsCardStep(false);
        if (hasAttachmentsStep) {
            setIsAttachmentsStep(true);
        } else {
            setCurrentPage(Math.min(cardStepAfterPage + 1, pages.length - 1));
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <div>
            {brand && (
                <p
                    className="mb-24"
                    style={{
                        fontFamily: "Georgia, 'Times New Roman', serif",
                        fontSize: "2.5rem",
                        color: "#D4881F",
                        lineHeight: 1.1,
                    }}
                >
                    {brand}
                </p>
            )}
            <div className="flex items-center justify-between mb-16">
                <p className="text-xs text-[rgba(11,19,43,0.5)] uppercase tracking-wide">
                    Step {activeStep} of {totalSteps}
                </p>
                <div className="flex gap-1">
                    {Array.from({ length: totalSteps }, (_, i) => i + 1).map(
                        (step) => (
                            <div
                                key={`stepper-dot-${step}`}
                                className={`h-1 w-8 rounded-full transition-colors ${
                                    step <= activeStep
                                        ? "bg-primary"
                                        : "bg-[rgba(11,19,43,0.12)]"
                                }`}
                            />
                        )
                    )}
                </div>
            </div>

            {paymentCardState?.isError && (
                <div className="mb-16 flex items-center justify-between gap-12 rounded-md border border-red-200 bg-red-50 p-12">
                    <p className="text-sm text-red-700">
                        We couldn&apos;t check your saved payment cards.
                    </p>
                    <Button
                        variant="secondary"
                        mode="outline"
                        onClick={paymentCardState.onRetry}
                        disabled={paymentCardState.isFetching}
                    >
                        Try again
                    </Button>
                </div>
            )}

            {isCardStep && paymentCardState && (
                <ConsentPaymentCardStep
                    renderForm={paymentCardState.renderForm}
                    onSaved={handleCardSaved}
                    acknowledgementText={
                        paymentCardStepConfig?.acknowledgementText
                    }
                    initials={cardStepInitials}
                    onInitialsChange={setCardStepInitials}
                    initialsLabel={paymentCardStepConfig?.initialsLabel}
                    additionalInitialsFields={
                        paymentCardStepConfig?.additionalInitialsFields
                    }
                    additionalInitials={cardStepAdditionalInitials}
                    onAdditionalInitialsChange={(key, value) =>
                        setCardStepAdditionalInitials((previous) => ({
                            ...previous,
                            [key]: value,
                        }))
                    }
                    fieldError={isCardStep ? fieldError : undefined}
                    onInteract={() => setFieldError("")}
                />
            )}

            {isAttachmentsStep && attachmentsStepConfig && (
                <ConsentAttachmentsStep
                    fields={attachmentsStepConfig.fields}
                    uploadedFieldIds={uploadedFieldIds}
                    initialUploadedFiles={uploadedFiles}
                    pendingFiles={pendingAttachmentFiles}
                    onPendingFilesChange={(fieldId, files) =>
                        setPendingAttachmentFiles((previous) => ({
                            ...previous,
                            [fieldId]: files,
                        }))
                    }
                    fieldError={fieldError}
                    onInteract={() => setFieldError("")}
                />
            )}

            {!isCardStep && !isAttachmentsStep && (
                <div className="mb-20">
                    <ConsentFormContent content={page.content} />
                </div>
            )}

            {!isCardStep && !isAttachmentsStep && (
                <ConsentPageFields
                    page={page}
                    currentPage={currentPage}
                    formType={formType}
                    variant={variant}
                    initials={currentInitials}
                    fullName={currentFullName}
                    checkbox={currentCheckbox}
                    date={currentDate}
                    dateOfBirth={currentDateOfBirth}
                    custodianFullName={currentCustodianFullName}
                    custodianDateOfBirth={currentCustodianDateOfBirth}
                    signature={pageSignatures[signatureKey] ?? ""}
                    additionalInitials={pageInitials}
                    additionalFullNames={pageFullName}
                    additionalSignatures={pageSignatures}
                    fieldError={fieldError}
                    onInitialsChange={(value) =>
                        setPageInitials((previous) => ({
                            ...previous,
                            [initialsKey]: value,
                        }))
                    }
                    onFullNameChange={(value) =>
                        setPageFullName((previous) => ({
                            ...previous,
                            [fullNameKey]: value,
                        }))
                    }
                    onCheckboxChange={(value) =>
                        setPageCheckboxes((previous) => ({
                            ...previous,
                            [checkboxKey]: value,
                        }))
                    }
                    multiCheckboxValues={pageMultiCheckboxes}
                    onMultiCheckboxChange={(key, value) =>
                        setPageMultiCheckboxes((previous) => ({
                            ...previous,
                            [key]: value,
                        }))
                    }
                    onDateChange={(value) =>
                        setPageDates((previous) => ({
                            ...previous,
                            [dateKey]: value,
                        }))
                    }
                    onDateOfBirthChange={(value) =>
                        setPageDatesOfBirth((previous) => ({
                            ...previous,
                            [dateOfBirthKey]: value,
                        }))
                    }
                    onCustodianFullNameChange={(value) =>
                        setPageCustodianFullNames((previous) => ({
                            ...previous,
                            [custodianFullNameKey]: value,
                        }))
                    }
                    onCustodianDateOfBirthChange={(value) =>
                        setPageCustodianDatesOfBirth((previous) => ({
                            ...previous,
                            [custodianDateOfBirthKey]: value,
                        }))
                    }
                    onSignatureChange={(value) =>
                        setPageSignatures((previous) => ({
                            ...previous,
                            [signatureKey]: value,
                        }))
                    }
                    onAdditionalInitialsChange={(key, value) =>
                        setPageInitials((previous) => ({
                            ...previous,
                            [key]: value,
                        }))
                    }
                    onAdditionalFullNameChange={(key, value) =>
                        setPageFullName((previous) => ({
                            ...previous,
                            [key]: value,
                        }))
                    }
                    onAdditionalSignatureChange={(key, value) =>
                        setPageSignatures((previous) => ({
                            ...previous,
                            [key]: value,
                        }))
                    }
                    onInteract={() => setFieldError("")}
                />
            )}

            {!isCardStep && isSubmitStep && (
                <div className="mt-20">
                    <FormCheckbox
                        id="email-consent-copy"
                        checked={emailCopyRequested}
                        onChange={(event) =>
                            setEmailCopyRequested(event.target.checked)
                        }
                        label="Email me a copy of my completed consent form."
                    />
                </div>
            )}

            <div className="flex items-center justify-between mt-20 pt-12 border-t border-strokelight">
                <Button
                    variant="secondary"
                    mode="outline"
                    onClick={handleBack}
                    disabled={
                        (!isCardStep &&
                            !isAttachmentsStep &&
                            currentPage === 0) ||
                        isBusy
                    }
                    className={
                        !isCardStep && !isAttachmentsStep && currentPage === 0
                            ? "invisible"
                            : ""
                    }
                    iconLeft={<ChevronLeftIcon />}
                >
                    Back
                </Button>

                {isCardStep && paymentCardState && (
                    <Button
                        variant="primary"
                        type="button"
                        disabled={
                            paymentCardState.isAddingCard ||
                            !paymentCardState.isReady
                        }
                        onClick={() => {
                            if (
                                paymentCardStepConfig &&
                                (!cardStepInitials.trim() ||
                                    paymentCardStepConfig.additionalInitialsFields?.some(
                                        (field) =>
                                            field.required !== false &&
                                            !cardStepAdditionalInitials[
                                                field.key
                                            ]?.trim()
                                    ))
                            ) {
                                setFieldError(
                                    "Please enter your initials to continue."
                                );
                                return;
                            }
                            const form = document.getElementById(
                                "consent-add-card-form"
                            ) as HTMLFormElement | null;
                            form?.requestSubmit();
                        }}
                    >
                        {paymentCardState.isAddingCard
                            ? "Saving card..."
                            : "Save card"}
                    </Button>
                )}
                {!isCardStep && isSubmitStep && (
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isBusy}
                    >
                        {isBusy ? "Submitting..." : "Submit consent"}
                    </Button>
                )}
                {!isCardStep && !isSubmitStep && (
                    <Button
                        variant="primary"
                        onClick={handleNext}
                        disabled={isBusy}
                        iconRight={<ChevronRightIcon />}
                    >
                        {nextButtonLabel}
                    </Button>
                )}
            </div>
        </div>
    );
}
